import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import CostFactors from "../components/site/CostFactors";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";
import { Link } from "react-router-dom";
import {
  Droplet,
  Wind,
  Sparkles,
  ChefHat,
  Search,
  Wrench,
  RefreshCw,
  FileCheck2,
  Trash2,
  ClipboardCheck,
} from "lucide-react";
import QuoteForm from "../components/site/QuoteForm";

const IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/pnnuoixs_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_57_18%20PM.png";

const TYPES = [
  { icon: Droplet, title: "Water", copy: "Suitable for a range of general-purpose environments where water is safe to use." },
  { icon: Wind, title: "CO₂", copy: "Commonly used for electrical equipment and specific higher-risk areas." },
  { icon: Sparkles, title: "Dry Powder", copy: "A versatile extinguisher option depending on the site and its activities." },
  { icon: ChefHat, title: "Wet Chemical", copy: "Typically used where cooking oils and fats are present." },
];

const SERVICES = [
  { icon: Search, title: "Site surveys", copy: "On-site assessment to help identify the appropriate extinguisher provision." },
  { icon: Wrench, title: "Installation", copy: "Professional installation of extinguishers in accessible, well-signed locations." },
  { icon: ClipboardCheck, title: "Annual servicing", copy: "Scheduled annual servicing to help keep your extinguishers ready for use." },
  { icon: RefreshCw, title: "Replacement", copy: "Replacement of extinguishers that have reached end of life or are unfit for use." },
  { icon: FileCheck2, title: "Certification", copy: "Documentation issued following completed servicing." },
  { icon: Trash2, title: "Disposal of obsolete extinguishers", copy: "Responsible disposal of expired or unfit extinguishers." },
];

export default function FireExtinguishers() {
  useSEO({
    title: "Fire Extinguisher Servicing Ireland | Supply, Installation & Replacement",
    description:
      "Supply, installation, annual servicing, replacement and certification of fire extinguishers for businesses across Ireland. Book a survey or annual service with FSS.",
    canonicalPath: "/services/fire-extinguishers",
  });

  return (
    <>
      <PageHero
        eyebrow="Fire Extinguishers"
        title="Fire Extinguishers"
        subtitle="Fire Safety Systems supplies, installs, services and replaces fire extinguishers for businesses and organisations throughout Ireland. The appropriate extinguisher type should be selected based on the risks and activities within the premises."
        image={IMG}
        imageAlt="Commercial premises where fire extinguisher provision is essential"
        ctaPrimary={{ to: "/contact", label: "Request a Quote" }}
        ctaSecondary={{ to: "/contact", label: "Book a Service" }}
      />

      {/* Extinguisher types */}
      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="fss-overline">Extinguisher types we supply</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              The right extinguisher for the right risk
            </h2>
            <p className="mt-3 text-slate-600">
              We can provide the following extinguisher types. The most
              appropriate options for your premises should be confirmed through
              a site survey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TYPES.map((t, i) => (
              <div key={i} className="fss-card fss-card-hover" data-testid={`ext-type-${i}`}>
                <t.icon className="w-9 h-9 text-red-600" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.copy}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-5 bg-slate-50 border-l-4 border-slate-900 text-sm text-slate-600 leading-relaxed max-w-3xl">
            <strong className="text-slate-900">Please note:</strong> the correct
            extinguisher type depends on the specific risks and activities on
            each site. We recommend a site survey before selecting the final
            extinguisher provision for your premises.
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="fss-overline">Our extinguisher services</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Supply, service and replace
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-white p-7" data-testid={`ext-service-${i}`}>
                <s.icon className="w-7 h-7 text-green-700" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="fss-section bg-white">
        <div className="fss-container max-w-3xl">
          <div className="text-center mb-10">
            <div className="fss-overline">Book a service</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Arrange a fire extinguisher survey or annual service
            </h2>
            <p className="mt-3 text-slate-600">
              Complete the form below and a member of our team will be in touch.
            </p>
          </div>
          <QuoteForm defaultService="Fire Extinguishers" />
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="fss-btn fss-btn-outline">Contact FSS</Link>
          </div>
        </div>
      </section>

      <CostFactors
        title="What affects fire extinguisher servicing cost"
        intro="Servicing costs depend on the number, type and condition of the extinguishers on site."
        factors={[
          "Number of extinguishers on site",
          "Types of extinguishers being serviced",
          "Age and condition of the existing units",
          "Whether replacements or refills are required",
          "Site access and time required on site",
          "Number of sites within a coordinated service",
          "Any additional signage or positioning work",
          "Disposal of obsolete or unfit extinguishers",
        ]}
      />

      <FAQBlock
        eyebrow="Fire extinguisher FAQ"
        title="Frequently asked questions"
        items={[
          { q: "How often should extinguishers be serviced?", a: "Extinguishers are usually serviced annually with periodic checks between visits. We will confirm an appropriate schedule for your premises." },
          { q: "What extinguisher types does FSS supply?", a: "Water, CO₂, Dry Powder and Wet Chemical extinguishers. The right type for your premises depends on the risks and activities on site — we recommend a survey before finalising the provision." },
          { q: "Can an extinguisher be refilled?", a: "Where refill is practical and supported by the manufacturer we can arrange it. In many cases replacement is more appropriate — we will advise based on condition." },
          { q: "What happens to obsolete extinguishers?", a: "We arrange responsible disposal of obsolete or unfit extinguishers as part of the service." },
          { q: "How many extinguishers does a business need?", a: "The correct number depends on floor area, layout, fire risks and building use. We confirm this during a site survey." },
          { q: "Can FSS service extinguishers supplied by another company?", a: "In most cases yes — we can inspect the existing units and advise what is required." },
          { q: "What affects the servicing cost?", a: "The number and type of extinguishers, their condition, site access, and whether any replacements or disposals are required." },
          { q: "Is certification provided?", a: "We provide documentation covering the extinguishers serviced and any recommended follow-up actions." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Arrange a fire extinguisher survey or annual service."
        subtitle="Talk to our team about extinguisher provision for your premises."
        primary={{ to: "/contact", label: "Request a Quote" }}
        secondary={{ to: "/contact", label: "Book a Service" }}
      />
    </>
  );
}
